import {createSlice, isAnyOf} from '@reduxjs/toolkit';
import {createMeeting, loadMeetings} from '../meetings/actions';
import {
  loadAvailableMeetingRoomsWithCapacityFilter,
  loadAvailableMeetingRooms,
  loadAllMeetingRooms,
  markAsBookedLocally,
  loadBuildingMeetingRoomAmenities,
} from './actions';
import {meetingRoomAdapter} from './adapters';
import {getInitialState} from './initialState';

const initialState = getInitialState();

const slice = createSlice({
  name: 'meetingRooms',
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadAvailableMeetingRoomsWithCapacityFilter.pending, (state) => {
        state.capacityFilteredAvailableMeetingRoomsLoadingStatus = 'Loading';
      })
      .addCase(loadAvailableMeetingRoomsWithCapacityFilter.fulfilled, (state, {payload}) => {
        state.capacityFilteredAvailableMeetingRoomsLoadingStatus = 'Loaded';
        state.capacityFilteredAvailableMeetingRoomIds = Object.keys(payload.meetingRooms ?? {});
      })
      .addCase(loadAvailableMeetingRoomsWithCapacityFilter.rejected, (state) => {
        state.capacityFilteredAvailableMeetingRoomsLoadingStatus = 'Failed';
      })
      .addCase(loadAvailableMeetingRooms.pending, (state) => {
        state.availableMeetingRoomLoadingStatus = 'Loading';
      })
      .addCase(loadAvailableMeetingRooms.fulfilled, (state, {payload}) => {
        state.availableMeetingRoomLoadingStatus = 'Loaded';
        state.availableMeetingRoomIds = Object.keys(payload.meetingRooms ?? {});
      })
      .addCase(loadAvailableMeetingRooms.rejected, (state) => {
        state.availableMeetingRoomLoadingStatus = 'Failed';
      })
      .addCase(loadAllMeetingRooms.pending, (state, {meta}) => {
        const {buildingId, startDateTime, endDateTime} = meta.arg;
        const previousEntry = state.meetingRoomSuggestionsByBuilding[buildingId];

        state.meetingRoomSuggestionsByBuilding[buildingId] = {
          status: 'loading',
          buildingId,
          // Keep previously loaded resources when we refresh
          data: previousEntry?.data ?? {},
          meetingRoomIds: previousEntry?.meetingRoomIds ?? [],
          roombookingStartDateTime: startDateTime,
          roombookingEndDateTime: endDateTime,
          timestamp: new Date().toISOString(),
          // Keep previous error message while we refresh
          error: undefined,
        };
      })
      .addCase(loadAllMeetingRooms.fulfilled, (state, {payload, meta}) => {
        const {buildingId} = meta.arg;
        const previousEntry = state.meetingRoomSuggestionsByBuilding[buildingId];

        const payloadWithFallback = payload.meetingRooms ?? {};

        state.meetingRoomSuggestionsByBuilding[buildingId] = {
          ...previousEntry!,
          status: 'loaded',
          error: undefined,
          data: payload.suggestionData,
          meetingRoomIds: Object.keys(payloadWithFallback),
        };
      })
      .addCase(loadAllMeetingRooms.rejected, (state, {meta, error}) => {
        const {buildingId} = meta.arg;
        const previousEntry = state.meetingRoomSuggestionsByBuilding[buildingId];

        state.meetingRoomSuggestionsByBuilding[buildingId] = {
          ...previousEntry!,
          status: 'failed',
          error: error.message ?? 'Failed to load suggestions',
        };
      })
      .addCase(markAsBookedLocally, (state, {payload}) => {
        const {buildingId, roomId} = payload;
        const previousEntry = state.meetingRoomSuggestionsByBuilding[buildingId];
        const currentSuggestion = previousEntry?.data[roomId];

        if (previousEntry && currentSuggestion) {
          state.meetingRoomSuggestionsByBuilding[buildingId] = {
            ...previousEntry,
            data: {
              ...previousEntry.data,
              [roomId]: {
                ...currentSuggestion,
                markedAsBookedLocally: true,
              },
            },
          };
        }
      })
      .addCase(loadBuildingMeetingRoomAmenities.pending, (state) => {
        state.buildingMeetingRoomAmenities.status = 'Loading';
      })
      .addCase(loadBuildingMeetingRoomAmenities.fulfilled, (state, {payload}) => {
        state.buildingMeetingRoomAmenities.status = 'Loaded';
        state.buildingMeetingRoomAmenities.meetingRoomAmenitiesByBuilding = payload;
      })
      .addCase(loadBuildingMeetingRoomAmenities.rejected, (state, {meta, error}) => {
        state.buildingMeetingRoomAmenities.status = 'Failed';
      })
      .addMatcher(
        isAnyOf(
          createMeeting.fulfilled,
          loadMeetings.fulfilled,
          loadAvailableMeetingRoomsWithCapacityFilter.fulfilled,
          loadAvailableMeetingRooms.fulfilled,
          loadAllMeetingRooms.fulfilled,
        ),
        (state, {payload}) => {
          meetingRoomAdapter.upsertMany(state.meetingRooms, payload.meetingRooms ?? {});
        },
      );
  },
});

export const {reducer: meetingRoomReducer} = slice;
